import { useState } from 'react';
import { Clock, User, MessageSquare, CheckCircle, Lock, Pencil, Trash2, Loader2, X, Check, AlertTriangle, ArrowDown, Paperclip } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { editComment, deleteCommentApi } from '@/hooks/useTickets';
import { buildStorageComment, toDisplayComment, extractMentionedIds } from '@/lib/mentions';
import type { TicketUpdateItem } from '@uniticket/shared';

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) +
    ', ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

interface Props {
  updates: TicketUpdateItem[];
  ticketId: string;
  onRefresh: () => void;
  userMap?: Map<string, string>;
}

function renderCommentWithMentions(text: string, userMap?: Map<string, string>) {
  if (!userMap || userMap.size === 0) return text;
  const parts = text.split(/(@\{[0-9a-f-]{36}\})/g);
  if (parts.length === 1) return text; // No mention tokens found
  return parts.map((part, i) => {
    const match = part.match(/^@\{([0-9a-f-]{36})\}$/);
    if (match) {
      const name = userMap.get(match[1]) || 'Unknown User';
      return (
        <span key={i} className="inline-flex items-center bg-brand-purple/10 text-brand-purple font-semibold px-1.5 py-0.5 rounded text-xs">
          @{name}
        </span>
      );
    }
    return part;
  });
}

export function TicketTimeline({ updates, ticketId, onRefresh, userMap }: Props) {
  const { t } = useTranslation(['tickets', 'common']);
  const { user, isInternal, isAdmin } = useAuth();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [editOriginalIds, setEditOriginalIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  // Filter out internal notes for external users (should already be done server-side, but defense in depth)
  const events = isInternal
    ? updates
    : updates.filter((e) => !e.isInternal);

  // Newest first
  const sortedEvents = [...events].reverse();

  function isCommentAuthor(eventUserId: string): boolean {
    return eventUserId === user?.id;
  }

  function startEdit(event: TicketUpdateItem) {
    setEditingId(event.id);
    const raw = event.comment ?? '';
    // Show readable names instead of raw @{uuid} tokens while editing. Fall back
    // to the raw text if the user map hasn't loaded, so editing still round-trips.
    setEditText(userMap && userMap.size > 0 ? toDisplayComment(raw, userMap) : raw);
    setEditOriginalIds(extractMentionedIds(raw));
  }

  function cancelEdit() {
    setEditingId(null);
    setEditText('');
    setEditOriginalIds([]);
  }

  async function handleSaveEdit(commentId: string) {
    if (!editText.trim()) return;
    setSaving(true);
    try {
      // Convert @DisplayName back to @{uuid} tokens for the mentions that were
      // already in this comment, so the timeline keeps rendering pills.
      const comment = userMap && userMap.size > 0
        ? buildStorageComment(editText.trim(), editOriginalIds, userMap)
        : editText.trim();
      await editComment(ticketId, commentId, { comment });
      toast.success(t('tickets:timeline.commentUpdated'));
      setEditingId(null);
      setEditText('');
      setEditOriginalIds([]);
      onRefresh();
    } catch (err: any) {
      toast.error(err?.message || t('tickets:timeline.updateError'));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(commentId: string) {
    if (!window.confirm(t('tickets:timeline.deleteConfirm'))) return;
    try {
      await deleteCommentApi(ticketId, commentId);
      toast.success(t('tickets:timeline.commentDeleted'));
      onRefresh();
    } catch (err: any) {
      toast.error(err?.message || t('tickets:timeline.deleteError'));
    }
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
      <div className="px-6 py-4 border-b border-slate-100">
        <h3 className="text-sm font-bold text-slate-800">{t('tickets:timeline.title')}</h3>
      </div>
      <div className="px-6 py-4 space-y-0">
        {sortedEvents.length === 0 && (
          <p className="text-sm text-slate-400 py-4 text-center">{t('tickets:timeline.noActivity')}</p>
        )}
        {sortedEvents.map((event, index) => {
          const isLast = index === sortedEvents.length - 1;
          const displayName = event.user.displayName;
          const initials = displayName.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);

          if (event.action === 'COMMENT') {
            const canEdit = isCommentAuthor(event.userId);
            const canDelete = isCommentAuthor(event.userId) || isAdmin;
            const isEditing = editingId === event.id;
            return (
              <div key={event.id} className="relative pl-10 pb-6">
                {!isLast && <div className="absolute left-4 top-8 bottom-0 w-px bg-slate-200" />}
                <div className="absolute left-0 top-0 w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-[10px] font-bold text-slate-600 z-10">
                  {initials}
                </div>
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-100">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-semibold text-slate-800">
                      {displayName} <span className="font-normal text-slate-400">{t('tickets:timeline.commented')}</span>
                    </p>
                    <div className="flex items-center gap-2">
                      {!isEditing && canEdit && (
                        <button onClick={() => startEdit(event)} className="p-1 text-slate-300 hover:text-slate-500 transition-colors" title={t('tickets:timeline.editComment')}>
                          <Pencil className="w-3 h-3" />
                        </button>
                      )}
                      {!isEditing && canDelete && (
                        <button onClick={() => handleDelete(event.id)} className="p-1 text-slate-300 hover:text-red-400 transition-colors" title={t('tickets:timeline.deleteComment')}>
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                      <p className="text-xs text-slate-400">{formatTimestamp(event.createdAt)}</p>
                    </div>
                  </div>
                  {isEditing ? (
                    <div className="space-y-2">
                      <textarea
                        rows={3}
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40 resize-y"
                        disabled={saving}
                      />
                      <div className="flex items-center gap-2 justify-end">
                        <button onClick={cancelEdit} disabled={saving} className="px-3 py-1 text-xs font-medium text-slate-500 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors flex items-center gap-1">
                          <X className="w-3 h-3" /> {t('common:actions.cancel')}
                        </button>
                        <button
                          onClick={() => handleSaveEdit(event.id)}
                          disabled={saving || !editText.trim()}
                          className="px-3 py-1 text-xs font-semibold text-white rounded-lg bg-gradient-to-r from-brand-rose to-brand-orange hover:from-brand-purple hover:to-brand-rose transition-all disabled:opacity-50 flex items-center gap-1"
                        >
                          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                          {saving ? t('common:actions.saving') : t('common:actions.save')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{renderCommentWithMentions(event.comment ?? '', userMap)}</p>
                  )}
                </div>
              </div>
            );
          }

          if (event.action === 'INTERNAL_NOTE') {
            const canEdit = isCommentAuthor(event.userId);
            const canDelete = isCommentAuthor(event.userId) || isAdmin;
            const isEditing = editingId === event.id;
            return (
              <div key={event.id} className="relative pl-10 pb-6">
                {!isLast && <div className="absolute left-4 top-8 bottom-0 w-px bg-slate-200" />}
                <div className="absolute left-0 top-0 w-8 h-8 rounded-full bg-brand-purple/20 flex items-center justify-center z-10">
                  <Lock className="w-3.5 h-3.5 text-brand-purple" />
                </div>
                <div className="internal-note rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-brand-purple">
                        {displayName} <span className="font-normal text-brand-purple/60">{t('tickets:timeline.internalNote')}</span>
                      </p>
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-brand-purple/10 text-brand-purple border border-brand-purple/20">
                        <Lock className="w-2.5 h-2.5" />
                        {t('tickets:timeline.internalBadge')}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {!isEditing && canEdit && (
                        <button onClick={() => startEdit(event)} className="p-1 text-brand-purple/30 hover:text-brand-purple/60 transition-colors" title={t('tickets:timeline.editNote')}>
                          <Pencil className="w-3 h-3" />
                        </button>
                      )}
                      {!isEditing && canDelete && (
                        <button onClick={() => handleDelete(event.id)} className="p-1 text-brand-purple/30 hover:text-red-400 transition-colors" title={t('tickets:timeline.deleteNote')}>
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                      <p className="text-xs text-brand-purple/50">{formatTimestamp(event.createdAt)}</p>
                    </div>
                  </div>
                  {isEditing ? (
                    <div className="space-y-2">
                      <textarea
                        rows={3}
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        className="w-full px-3 py-2 text-sm bg-white border border-brand-purple/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-purple/20 focus:border-brand-purple/40 resize-y"
                        disabled={saving}
                      />
                      <div className="flex items-center gap-2 justify-end">
                        <button onClick={cancelEdit} disabled={saving} className="px-3 py-1 text-xs font-medium text-slate-500 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors flex items-center gap-1">
                          <X className="w-3 h-3" /> {t('common:actions.cancel')}
                        </button>
                        <button
                          onClick={() => handleSaveEdit(event.id)}
                          disabled={saving || !editText.trim()}
                          className="px-3 py-1 text-xs font-semibold text-white rounded-lg bg-brand-purple hover:bg-brand-purple/90 transition-all disabled:opacity-50 flex items-center gap-1"
                        >
                          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                          {saving ? t('common:actions.saving') : t('common:actions.save')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-brand-purple/80 leading-relaxed whitespace-pre-wrap">{renderCommentWithMentions(event.comment ?? '', userMap)}</p>
                  )}
                </div>
              </div>
            );
          }

          if (event.action === 'STATUS_CHANGE') {
            return (
              <div key={event.id} className="relative pl-10 pb-6">
                {!isLast && <div className="absolute left-4 top-8 bottom-0 w-px bg-slate-200" />}
                <div className="absolute left-0 top-0 w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center z-10">
                  <Clock className="w-3.5 h-3.5 text-amber-600" />
                </div>
                <div className="py-2">
                  <p className="text-sm text-slate-600">
                    <span className="font-semibold text-slate-800">{displayName}</span>{' '}
                    {t('tickets:timeline.statusChanged', { from: event.oldValue, to: event.newValue })}
                  </p>
                  <p className="text-xs text-slate-400 mt-0.5">{formatTimestamp(event.createdAt)}</p>
                </div>
              </div>
            );
          }

          if (event.action === 'ASSIGNMENT') {
            return (
              <div key={event.id} className="relative pl-10 pb-6">
                {!isLast && <div className="absolute left-4 top-8 bottom-0 w-px bg-slate-200" />}
                <div className="absolute left-0 top-0 w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center z-10">
                  <User className="w-3.5 h-3.5 text-blue-600" />
                </div>
                <div className="py-2">
                  <p className="text-sm text-slate-600">
                    <span className="font-semibold text-slate-800">{displayName}</span> {t('tickets:timeline.assigned')}
                  </p>
                  <p className="text-xs text-slate-400 mt-0.5">{formatTimestamp(event.createdAt)}</p>
                </div>
              </div>
            );
          }

          if (event.action === 'PRIORITY_CHANGE') {
            return (
              <div key={event.id} className="relative pl-10 pb-6">
                {!isLast && <div className="absolute left-4 top-8 bottom-0 w-px bg-slate-200" />}
                <div className="absolute left-0 top-0 w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center z-10">
                  <MessageSquare className="w-3.5 h-3.5 text-orange-600" />
                </div>
                <div className="py-2">
                  <p className="text-sm text-slate-600">
                    <span className="font-semibold text-slate-800">{displayName}</span>{' '}
                    {t('tickets:timeline.priorityChanged', { from: event.oldValue, to: event.newValue })}
                  </p>
                  <p className="text-xs text-slate-400 mt-0.5">{formatTimestamp(event.createdAt)}</p>
                </div>
              </div>
            );
          }

          if (event.action === 'ESCALATION') {
            return (
              <div key={event.id} className="relative pl-10 pb-6">
                {!isLast && <div className="absolute left-4 top-8 bottom-0 w-px bg-slate-200" />}
                <div className="absolute left-0 top-0 w-8 h-8 rounded-full bg-red-100 flex items-center justify-center z-10">
                  <AlertTriangle className="w-3.5 h-3.5 text-red-600" />
                </div>
                <div className="py-2">
                  <p className="text-sm text-slate-600">
                    <span className="font-semibold text-slate-800">{displayName}</span> {t('tickets:timeline.escalatedTicket')}
                  </p>
                  {event.comment && (
                    <div className="mt-1.5 pl-3 border-l-2 border-red-200 text-sm text-slate-600 whitespace-pre-wrap">
                      {event.comment}
                    </div>
                  )}
                  <p className="text-xs text-slate-400 mt-0.5">{formatTimestamp(event.createdAt)}</p>
                </div>
              </div>
            );
          }

          if (event.action === 'DE_ESCALATION') {
            return (
              <div key={event.id} className="relative pl-10 pb-6">
                {!isLast && <div className="absolute left-4 top-8 bottom-0 w-px bg-slate-200" />}
                <div className="absolute left-0 top-0 w-8 h-8 rounded-full bg-green-100 flex items-center justify-center z-10">
                  <ArrowDown className="w-3.5 h-3.5 text-green-600" />
                </div>
                <div className="py-2">
                  <p className="text-sm text-slate-600">
                    <span className="font-semibold text-slate-800">{displayName}</span> {t('tickets:timeline.deescalatedTicket')}
                  </p>
                  {event.comment && (
                    <div className="mt-1.5 pl-3 border-l-2 border-green-200 text-sm text-slate-600 whitespace-pre-wrap">
                      {event.comment}
                    </div>
                  )}
                  <p className="text-xs text-slate-400 mt-0.5">{formatTimestamp(event.createdAt)}</p>
                </div>
              </div>
            );
          }

          if (event.action === 'FIELD_CHANGE') {
            return (
              <div key={event.id} className="relative pl-10 pb-6">
                {!isLast && <div className="absolute left-4 top-8 bottom-0 w-px bg-slate-200" />}
                <div className="absolute left-0 top-0 w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center z-10">
                  <Pencil className="w-3.5 h-3.5 text-slate-500" />
                </div>
                <div className="py-2">
                  <p className="text-sm text-slate-600">
                    <span className="font-semibold text-slate-800">{displayName}</span>{' '}
                    {t('tickets:timeline.updatedField', { field: event.fieldName })}
                  </p>
                  <p className="text-xs text-slate-400 mt-0.5">{formatTimestamp(event.createdAt)}</p>
                </div>
              </div>
            );
          }

          if (event.action === 'ATTACHMENT_ADDED') {
            return (
              <div key={event.id} className="relative pl-10 pb-6">
                {!isLast && <div className="absolute left-4 top-8 bottom-0 w-px bg-slate-200" />}
                <div className="absolute left-0 top-0 w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center z-10">
                  <Paperclip className="w-3.5 h-3.5 text-blue-600" />
                </div>
                <div className="py-2">
                  <p className="text-sm text-slate-600">
                    <span className="font-semibold text-slate-800">{displayName}</span>{' '}
                    {t('tickets:timeline.attachedFile', { file: event.newValue })}
                  </p>
                  <p className="text-xs text-slate-400 mt-0.5">{formatTimestamp(event.createdAt)}</p>
                </div>
              </div>
            );
          }

          if (event.action === 'ATTACHMENT_DELETED') {
            return (
              <div key={event.id} className="relative pl-10 pb-6">
                {!isLast && <div className="absolute left-4 top-8 bottom-0 w-px bg-slate-200" />}
                <div className="absolute left-0 top-0 w-8 h-8 rounded-full bg-red-100 flex items-center justify-center z-10">
                  <Trash2 className="w-3.5 h-3.5 text-red-600" />
                </div>
                <div className="py-2">
                  <p className="text-sm text-slate-600">
                    <span className="font-semibold text-slate-800">{displayName}</span>{' '}
                    {t('tickets:timeline.removedAttachment', { file: event.newValue })}
                  </p>
                  <p className="text-xs text-slate-400 mt-0.5">{formatTimestamp(event.createdAt)}</p>
                </div>
              </div>
            );
          }

          // CREATED or other
          return (
            <div key={event.id} className="relative pl-10 pb-2">
              <div className="absolute left-0 top-0 w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center z-10">
                <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />
              </div>
              <div className="py-2">
                <p className="text-sm text-slate-600">
                  <span className="font-semibold text-slate-800">{displayName}</span> {t('tickets:timeline.createdTicket')}
                </p>
                <p className="text-xs text-slate-400 mt-0.5">{formatTimestamp(event.createdAt)}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
